import FormattedMessage from 'I18n/components/FormattedMessage';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LeftIcon } from 'foundations-assets';
import apiClient from 'hub-http/clients/apiClient';
import { useContext, useEffect, useMemo, useState } from 'react';
import debounce from 'transmute/debounce';
import identity from 'transmute/identity';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import KBArticlesTypeahead from './KBArticlesTypeahead';
import MultiKBTypeaheadContainer from './MultiKBTypeaheadContainer';
import * as KnowledgeArticlesApi from './draft-content-transplant-ka/KnowledgeArticlesApi';
import KnowledgePopoverErrorState from './draft-content-transplant-ka/KnowledgePopoverErrorState';
import { useFetchArticles, useFetchBusinessUnits, useFetchKnowledgeBases } from './kbQueries';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { H7 } from 'foundations-components/transitional/components/H7';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VerticalSpacer = () => {
  return /*#__PURE__*/_jsx(View, {
    blockSize: "24px",
    inlineSize: "1px",
    marginInlineEnd: "200",
    backgroundColor: "border-core-default"
  });
};
const MultiKBs = ({
  onClickBack,
  onSearchAllKBs,
  onSelectArticle,
  onSelectKB,
  portalId
}) => {
  const {
    KNOWLEDGE_ARTICLE: {
      onInsertContent = identity
    } = {}
  } = useContext(EditorConfigContext);
  const {
    data: buData
  } = useFetchBusinessUnits();
  const {
    data: kbData,
    loading: kbsAreLoading,
    error: kbError
  } = useFetchKnowledgeBases();
  const [fetchArticles, {
    loading: articlesAreLoading,
    data: articleData
  }] = useFetchArticles();
  const kbOptions = useMemo(() => {
    if (!kbData || !buData) return [];
    if (buData.businessUnits.length <= 1) {
      return kbData.map(kb => ({
        text: kb.hs_name,
        value: kb.hs_id
      }));
    } else {
      return buData.businessUnits.map(bu => {
        return {
          options: kbData.filter(kb => kb.hs_business_unit_id === bu.id).map(kb => ({
            text: kb.hs_name,
            value: kb.hs_id
          })),
          text: bu.name,
          value: bu.id
        };
      });
    }
  }, [buData, kbData]);
  const articlesOptions = useMemo(() => {
    return kbData && articleData ? articleData.map(article => {
      var _kbData$find;
      return {
        help: (_kbData$find = kbData.find(kb => kb.hs_id === article.hs_group_id)) === null || _kbData$find === void 0 ? void 0 : _kbData$find.hs_name,
        text: article.hs_name,
        value: article.hs_id
      };
    }) : [];
  }, [articleData, kbData]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKB, setSelectedKB] = useState(undefined);
  const apiFunctions = useMemo(() => {
    if (!selectedKB) return null;
    return KnowledgeArticlesApi.defaultApiWrapper(apiClient, selectedKB.version, selectedKB.hs_id);
  }, [selectedKB]);
  const kbArticlesOnClickBack = useMemo(() => {
    return kbOptions.length === 1 ? onClickBack : () => setSelectedKB(undefined);
  }, [kbOptions.length, onClickBack]);
  useEffect(() => {
    if (kbData && kbData.length === 1) {
      setSelectedKB(kbData[0]);
    }
  }, [kbData]);
  const onSearchChange = useMemo(() => debounce(200, e => {
    setSearchQuery(e.target.value);
    if (e.target.value.length) {
      fetchArticles({
        variables: {
          query: e.target.value
        }
      });
      if (onSearchAllKBs) onSearchAllKBs();
    }
  }), [fetchArticles, onSearchAllKBs]);
  if (selectedKB !== undefined && apiFunctions) {
    return /*#__PURE__*/_jsx(KBArticlesTypeahead, {
      onChange: onSelectArticle,
      apiFunctions: apiFunctions,
      knowledgeBaseId: selectedKB.hs_id,
      onClickBack: kbArticlesOnClickBack,
      portalId: portalId,
      title: selectedKB.hs_name
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    style: {
      minHeight: 322
    },
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      alignItems: "center",
      marginBlockEnd: "300",
      paddingInlineStart: "400",
      children: [/*#__PURE__*/_jsx(IconButton, {
        "data-test-id": "knowledge-article-back-button",
        use: "link",
        onClick: onClickBack,
        children: /*#__PURE__*/_jsx(LeftIcon, {})
      }), /*#__PURE__*/_jsx(VerticalSpacer, {}), /*#__PURE__*/_jsx(H7, {
        margin: "0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'hubspot-prosemirror.insertMenu.knowledgeArticle'
        })
      })]
    }), kbError ? /*#__PURE__*/_jsx(KnowledgePopoverErrorState, {}) : /*#__PURE__*/_jsx(MultiKBTypeaheadContainer, {
      kbsAreLoading: kbsAreLoading,
      onChange: e => {
        if (searchQuery.length) {
          const article = articleData.find(a => a.hs_id === e.target.value);
          onSelectArticle({
            htmlBody: `<a href=${article.hs_absolute_url}>${article.hs_name}</a>`
          });
          onInsertContent();
        } else {
          if (onSelectKB) onSelectKB();
          setSelectedKB(kbData.find(kb => kb.hs_id === e.target.value));
        }
      },
      onSearchChange: onSearchChange,
      options: searchQuery.length === 0 ? kbOptions : articlesOptions,
      portalId: portalId,
      searchQuery: searchQuery,
      searchIsLoading: articlesAreLoading
    })]
  });
};
export default MultiKBs;